import * as Sentry from "@sentry/react";
import { isCancelledError } from "@tanstack/react-query";
import { SessionHandler } from "verdi-web-client";

// Key factories are aliased to keep the fan-out below readable as one row per collection. They stay
// the generated factories, so an orval rename surfaces here as a broken import rather than a miss.
import { getGetAOIQueryKey as aoiDetailKey, getListAOIsQueryKey as aoiListKey } from "@/api/orval/generated/aoi";
import {
    getGetDeviceQueryKey as deviceDetailKey,
    getListDevicesQueryKey as deviceListKey,
} from "@/api/orval/generated/device";
import {
    getGetIrrigationEventQueryKey as eventDetailKey,
    getListIrrigationEventsQueryKey as eventListKey,
} from "@/api/orval/generated/irrigation-event";
import {
    getGetIrrigationEventGroupQueryKey as eventGroupDetailKey,
    getListIrrigationEventGroupsQueryKey as eventGroupListKey,
} from "@/api/orval/generated/irrigation-event-group";
import {
    getGetIrrigationSetQueryKey as irrigationSetDetailKey,
    getListIrrigationSetsQueryKey as irrigationSetListKey,
} from "@/api/orval/generated/irrigation-set";
import type { LoadSessionData200 } from "@/api/orval/generated/schemas";
import {
    getGetSensoterraDeviceQueryKey as sensoterraDeviceDetailKey,
    getListSensoterraDevicesQueryKey as sensoterraDeviceListKey,
} from "@/api/orval/generated/sensoterra-device";
import {
    getLoadSessionDataQueryOptions,
    getGetRecentSessionDataQueryKey as recentDataKey,
} from "@/api/orval/generated/session";
import {
    getGetThirdPartyDeviceQueryKey as thirdPartyDeviceDetailKey,
    getListThirdPartyDevicesQueryKey as thirdPartyDeviceListKey,
} from "@/api/orval/generated/third-party-device";
import { getGetZoneQueryKey as zoneDetailKey, getListZonesQueryKey as zoneListKey } from "@/api/orval/generated/zone";
import { seedCollection, seedQuery } from "@/api/query-cache/collection-cache-writers";
import { queryClient } from "@/api/queryClient";

/** Fetches the session aggregate and warms the cache. Never throws — a failure just leaves the cache unchanged. */
export async function prefetchSessionData(): Promise<void> {
    // The cache is scoped by curUser at write time so we pin the org we asked for
    const requestedOrg = SessionHandler.curUser;

    try {
        // fetchQuery, not a bare loadSessionData(), so the request goes through customFetch's date revival.
        const session = await queryClient.fetchQuery(getLoadSessionDataQueryOptions());
        // Check if the org has changed since the request was made and early return to avoid seeding the cache with outdated data.
        if (SessionHandler.curUser !== requestedOrg) return;

        seedQueryCacheFromSession(session);
    } catch (error) {
        // The map draws from this seed, so a failure here is a map with markers missing and nothing
        // on screen to say why. An org switch cancels this fetch through resetQueries on session
        // unload, which is not a failure to report.
        if (!isCancelledError(error)) Sentry.captureException(error);
        console.warn("Failed to prefetch session data", error);
    }
}

/**
 * Fans one session payload across the list and detail caches for each collection.
 *
 * A seed is a warm partial, not a stand-in: the session endpoint trims a few optional fields to keep
 * the bootstrap payload small, so a seeded row can be thinner than the same row fetched directly. It
 * reuses the list and detail DTOs verbatim, so thinner means a field reads as absent, never a wrong
 * type. Online the stale-on-arrival seed refetches and fills the gaps; offline nothing does.
 * loadSessionData in sessionControllerV2 owns the authoritative diff.
 */
export function seedQueryCacheFromSession(session: LoadSessionData200): void {
    seedCollection(aoiListKey(), aoiDetailKey, session.AreasOfInterest);
    seedCollection(zoneListKey(), zoneDetailKey, session.Zones);
    seedCollection(deviceListKey(), deviceDetailKey, session.Devices);
    seedCollection(thirdPartyDeviceListKey(), thirdPartyDeviceDetailKey, session.ThirdPartyDevices);
    seedCollection(sensoterraDeviceListKey(), sensoterraDeviceDetailKey, session.SensoterraDevices);
    seedCollection(irrigationSetListKey(), irrigationSetDetailKey, session.IrrigationSets, { enveloped: true });
    seedCollection(eventGroupListKey(), eventGroupDetailKey, session.CommandGroups, { enveloped: true });
    seedCollection(eventListKey(), eventDetailKey, session.RepeatingIrrigations, {
        enveloped: true,
        // Alone among these, /irrigation-events/{id} resolves the SCD recordId shared across a
        // schedule's versions rather than the per-version _id.
        toDetailId: (event) => event.recordId,
    });

    // A singleton response rather than an entity collection, so there is no detail query to pair with.
    seedQuery(recentDataKey(), session.RecentData);

    // Deliberately not seeded: LoraNetworkProfiles and CurUser/Users have no matching query, and
    // Images would be wrong rather than merely incomplete — the session returns a 60-day slice while
    // the unparameterized list key means every image, so seeding it shows a count that later grows.
}
